import type {
  AccessCatalog,
  CatalogSurface,
  ClientScopeKey,
  DemoClientsKey,
  SurfaceKey,
  TierKey,
} from "types/interfaces/access";

export interface GrantedLine {
  key: string;
  name: string;
  grants: string;
}

export type TierMap = Partial<Record<SurfaceKey, TierKey>>;

/** The tier a page gets: its own for a page open to everyone, else the group's. */
export function tierOn(surface: CatalogSurface, tiers: TierMap): TierKey {
  if (surface.everyone) return surface.tiers[0]?.tier ?? "none";
  return tiers[surface.key] ?? "none";
}

/** A sub-page opens inside its page, so it stays closed while that page is. */
export function parentClosed(
  catalog: AccessCatalog,
  tiers: TierMap,
  surface: CatalogSurface,
): CatalogSurface | null {
  if (!surface.parent) return null;
  const parent = catalog.surfaces.find((s) => s.key === surface.parent);
  if (!parent) return null;
  return tierOn(parent, tiers) === "none" ? parent : null;
}

/** One page's new tier; closing a page closes the sub-pages inside it too. */
export function withTier(
  catalog: AccessCatalog,
  tiers: TierMap,
  surface: SurfaceKey,
  tier: TierKey,
): TierMap {
  const next: TierMap = { ...tiers, [surface]: tier };
  if (tier === "none") {
    for (const child of catalog.surfaces) {
      if (child.parent === surface) next[child.key] = "none";
    }
  }
  return next;
}

/** One line per page the tiers reach, worded by the catalog - never by group name. */
export function grantedLines(
  catalog: AccessCatalog,
  tiers: TierMap,
): GrantedLine[] {
  const lines: GrantedLine[] = [];
  for (const surface of catalog.surfaces) {
    const tier = tierOn(surface, tiers);
    if (tier === "none") continue;
    const spec = surface.tiers.find((t) => t.tier === tier);
    if (!spec) continue;
    lines.push({ key: surface.key, name: surface.name, grants: spec.grants });
  }
  return lines;
}

export function noAccessNames(
  catalog: AccessCatalog,
  tiers: TierMap,
): string[] {
  return catalog.surfaces
    .filter((surface) => tierOn(surface, tiers) === "none")
    .map((surface) => surface.name);
}

/** A group's client rule in one line while it is edited; the server words a
 * saved group the same way (`applies_to`). */
export function appliesTo(
  scope: ClientScopeKey,
  teamName: string | null,
  demo: DemoClientsKey,
): string {
  if (scope === "team" && !teamName) return "No clients";
  if (demo === "only") {
    if (scope === "all") return "Every demo client";
    if (scope === "team") return `Every demo client serviced by ${teamName}`;
    return "Only assigned demo clients";
  }
  const line =
    scope === "all"
      ? "Every client"
      : scope === "team"
        ? `Every client serviced by ${teamName}`
        : "Only assigned clients";
  return demo === "exclude" ? `${line}, demo clients excluded` : line;
}
